import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BaseBlockView } from '../base-block.view';
import { LargeHeaderModel } from '../../../model/block';
import styled from 'styled-components';
import { getCDNUrl } from '../../../util/url/get-cdn-url.util';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../interface/component/injected-props.interface';
import { DraftToHtml } from '../../draft-to-html/draft-to-html';
import { action, computed, observable } from 'mobx';
import { LAYERS_DIV_ID } from '../../../constants';
import Waypoint from 'react-waypoint';
import { determineTextColor } from '../../../util/style/determine-text-color';
import { getImageURL } from '../../../util/url/get-image-url.util';
import { Ellipsis } from '../../ellipsis';
import { draftToString } from '../../../util/draft/draft-to-string.util';
import { ColorEnum } from '../../../enum/color.enum';
import { largeMobileMedia } from '../../../util/style/large-mobile-media';
import { getLinkFinalURL } from '../../../util/url/get-link-final-url';
import { CmsButton } from '../../cms-button/cms-button';
import { isDraftEmpty } from '../../../util/draft/is-draft-empty.util';
import { getCenterOfPageStyle } from '../../../util/style/get-center-of-page-style';
import {getImageCloudinaryURL} from '../../../util/url/get-image-cloudinary-url.util';

const STICKY_HEADER_HEIGHT = 73;
const HEADER_IMAGE_WIDTH = 315;
const HEADER_HEIGHT = 253;
const ViewContainer = styled.div`
  display:flex;
  flex-direction: column;
  ${(props: {wide: boolean}) => props.wide ? '' : getCenterOfPageStyle()(props)}
`;
const ImageAndTextContainer = styled.div`
  display:flex;
  min-height: ${HEADER_HEIGHT}px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  ${largeMobileMedia(`
    flex-direction: column;
    justify-content: stretch;
  `)}
`;
const ImageContainer = styled.div`
  display:flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 19px;
  width: ${HEADER_IMAGE_WIDTH}px;
  height: ${HEADER_HEIGHT}px;
  ${largeMobileMedia(`
    padding: 20px 37px 20px 37px;
    max-height: 154px;
  `)}
`;
const TextContainer = styled.div`
  display: flex;
  flex: 2 2 0px;
  flex-direction: column;
  min-height: ${HEADER_HEIGHT}px;
  padding: 35px 40px;
  ${largeMobileMedia(`
    padding: 20px 37px 20px 37px;
    width: 100%;
    flex: 0 0 auto;
  `)}
`;
const ButtonContainer = styled.div`
  display:flex;
  height: ${STICKY_HEADER_HEIGHT}px;
  justify-content: center;
  align-items: center;
`;
const LeftImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width:auto;
  height:auto;
`;

const TitleOuterContainer = styled.div`
    display: flex;
`;

const TitleContainer = styled.div`
    flex: 1 1 0px;
`;

const FixedLayoutContainer = styled.div`
  display: flex;
  height: ${STICKY_HEADER_HEIGHT}px;
  position: absolute;
  width:100%;
  z-index: 1000;
  align-items: center;
`;
const FixedLayoutInnerContainer = styled.div`
  display: flex;
  height: 100%;
  width:100%;
  align-items: center;
  ${(props: {wide: boolean}) => props.wide ? '' : getCenterOfPageStyle()(props)}
`;

const FixedButtonContainer = styled.div`
  margin-right: 12px;
  margin-left: 12px;
`;
const FixedTextContainer = styled.div`
  flex: 1 1 0px;
  justify-content: flex-start;
  min-width: 1px;
  & span {
    display: block;
  }
`;
const FixedLogoContainer = styled.div`
  padding-right: 12px;
  padding-left: 13px;
  width: 89px;
  background: ${ColorEnum.White};
  ${largeMobileMedia(`
    display: none;
  `)}
`;

const FixedImageContainer = styled.div`
  width: ${STICKY_HEADER_HEIGHT}px;
  height: ${STICKY_HEADER_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const SmallTextContainer = styled.div`
  font-size: 18px;
  color: ${(props: { color: string }) => props.color};
`;

@inject('state', 'history')
@observer
export class LargeHeaderView extends BaseBlockView<LargeHeaderModel, IInjectedProps> {
  state = {};
  @observable
  isFixed = false;
  @observable
  mounted = false;
  waypointMarker;

  @action
  onEnter = () => {
    this.isFixed = false;
  }
  @action
  onLeave = ({
               currentPosition
             }) => {
    if (currentPosition === 'above') {
      this.isFixed = true;
    }
    if (currentPosition === 'below') {
      this.isFixed = false;
    }
  }

  @computed
  get configuration() {
    return this.props.state.configuration;
  }

  @computed
  get image() {
    const {content} = this.props;
    if (!content.leftImage) {
      return null;
    }
    const imageLocation = getImageURL(this.props.state, content.leftImage, HEADER_IMAGE_WIDTH);
    if (imageLocation) {
      return <LeftImage src={getImageCloudinaryURL(getCDNUrl(this.props.state.configuration, imageLocation.location), HEADER_IMAGE_WIDTH, HEADER_HEIGHT, 'fit')}/>;
    }
  }

  @computed
  get smallImage() {
    const {content} = this.props;
    if (!content.leftImage) {
      return null;
    }
    const imageLocation = getImageURL(this.props.state, content.leftImage, STICKY_HEADER_HEIGHT);
    if (imageLocation) {
      return <LeftImage src={getImageCloudinaryURL(getCDNUrl(this.props.state.configuration, imageLocation.location), STICKY_HEADER_HEIGHT, STICKY_HEADER_HEIGHT, 'fit')}/>;
    }
  }

  @computed
  get applyButton() {
    const {content} = this.props;
    const {button} = content;
    if (!button || isDraftEmpty(button.text, this.props.state)) {
      return null;
    }
    return (
      <CmsButton
        text={draftToString(button.text, this.props.state)}
        color={button.backgroundColor}
        textColor={button.textColor}
        to={getLinkFinalURL(this.props.state, button.link)}
      />
    );
  }

  @computed
  get wayPoint() {
    if (!this.mounted) {
      return <div ref={(waypointMarker) => this.waypointMarker = waypointMarker}/>;
    }
    return (
      <Waypoint
        onEnter={this.onEnter}
        onLeave={this.onLeave}
      />
    );
  }

  @computed
  get fixedLayout() {
    const {content} = this.props;
    if (this.isFixed) {
      const element = document.getElementById(LAYERS_DIV_ID);
      if (element === null) {
        return undefined;
      }
      const backgroundColor = this.props.state.colors.getColor(content.bottomBackground);
      const textColor = this.props.state.colors.getColor(determineTextColor(backgroundColor));
      return ReactDOM.createPortal(
        <FixedLayoutContainer style={{backgroundColor: backgroundColor, color: textColor}}>
          <FixedLayoutInnerContainer wide={this.props.content.style === 'wide'}>
            {this.controlsLayout}
          </FixedLayoutInnerContainer>
        </FixedLayoutContainer>, document.getElementById(LAYERS_DIV_ID));
    }
  }

  @computed
  get controlsLayout() {
    const {content} = this.props;
    const textColor = this.props.state.colors.getColor(determineTextColor(this.props.state.colors.getColor(content.bottomBackground)));
    return (
      <>
        {this.isFixed ? <FixedLogoContainer>
          <FixedImageContainer>
            {this.smallImage}
          </FixedImageContainer>
        </FixedLogoContainer> : null}
        {this.isFixed ? <FixedTextContainer style={{marginLeft: 12}}>
          <SmallTextContainer color={textColor}>
            <Ellipsis>
              {draftToString(content.smallText, this.props.state)}
            </Ellipsis>
          </SmallTextContainer>
        </FixedTextContainer> : null}
        {this.props.content.showButton && <FixedButtonContainer>
          {this.applyButton}
        </FixedButtonContainer>}
      </>
    );
  }

  @action
  componentDidMount() {
    this.mounted = true;
    if (ReactDOM.findDOMNode(this.waypointMarker).getBoundingClientRect().top < 0) {
      this.isFixed = true;
    }
  }

  render() {
    const {content} = this.props;
    return (
      <>
        {this.fixedLayout}
        <ViewContainer data-type="LargeHeaderView" wide={this.props.content.style === 'wide'}>
          <ImageAndTextContainer>
            <ImageContainer>
              {this.image}
            </ImageContainer>
            <TextContainer style={{backgroundColor: this.props.state.colors.getColor(content.background)}}>
              <TitleOuterContainer>
                <TitleContainer>
                  <DraftToHtml
                    draft={content.largeText.text}
                    inEditor={this.props.inEditor}
                  />
                </TitleContainer>
              </TitleOuterContainer>
            </TextContainer>
          </ImageAndTextContainer>
          {this.wayPoint}
          {this.props.content.showButton &&
          <ButtonContainer style={{backgroundColor: this.props.state.colors.getColor(content.bottomBackground)}}>
            {this.controlsLayout}
          </ButtonContainer>}
        </ViewContainer>
      </>
    );
  }
}
